import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import { createAsyncThunk } from '@reduxjs/toolkit';
import Message from 'view/shared/message';
import {
  AvsConfiguration,
  AvsError,
  AvsUpdateConfigPayload,
  UpgradeType,
} from '../avs.types';
import AvsService from '../avs.service';

export const getAvsConfigurations = createAsyncThunk<
  AvsConfiguration[],
  { appToken: string },
  {
    state: RootState;
    rejectValue: AvsError | ErrorObject;
  }
>(
  'avs/get-configurations',
  async ({ appToken }, { getState, rejectWithValue }) => {
    try {
      const { environment: appEnvironment } = getState().root.global;

      return await AvsService.getAvsConfigurations({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const updateAvsConfigurations = createAsyncThunk<
  unknown,
  {
    appToken: string;
    data: AvsUpdateConfigPayload[];
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/update-configurations',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.updateAvsConfigurations({
        appToken,
        appEnvironment,
        data,
      });

      // re-fetch configuration list after successful update
      dispatch(getAvsConfigurations({ appToken }));

      return response;
    } catch (error: unknown) {
      const genericError =
        'Failed to update Account Verification configurations';
      Message.error(Errors.selectMessage(error) || genericError);

      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const requestServiceAccess = createAsyncThunk<
  unknown,
  {
    appToken: string;
    upgradeType: UpgradeType;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'mos/request-access',
  async ({ appToken, upgradeType }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.requestServiceAccess({
        appToken,
        appEnvironment,
        upgradeType,
      });
      let upgradeTypeText;
      if (upgradeType === UpgradeType.INTERNATIONAL_AVS) {
        upgradeTypeText = 'International account verification';
      } else {
        upgradeTypeText = 'Account verification';
      }
      Message.success(
        `${upgradeTypeText} access request submitted. ` +
          'Your account manager will reach out to you soon.',
      );
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);
